import { z } from "zod";

/**
 * The embedding model of an environment (ADR 0006): pinned, with no fallback, because vectors
 * from different models are not comparable. This is the single place that names it.
 *
 * - `modelId` and `dimension` default to the values below; an environment overrides them with
 *   `ESI_EMBEDDING_MODEL` and `ESI_EMBEDDING_DIMENSION` ({@link embeddingConfigFromEnv}).
 * - The `chunks.embedding vector(n)` column (packages/db/src/schema.ts) is generated from
 *   {@link EMBEDDING_COLUMN_DIMENSION}, so the database and the default always agree. A
 *   configured dimension that differs from the column fails at startup.
 * - Each tenant's `knowledge_index` row records the model and dimension its passages were
 *   written with, so a config change shows up as drift (`detectKnowledgeIndexDrift` in core).
 *
 * Changing the model: edit {@link DEFAULT_EMBEDDING}, run `mise run db:generate`, commit, deploy
 * (the schema is recreated while ESI is greenfield), then re-index every tenant.
 */
export const DEFAULT_EMBEDDING = {
  /** Cohere Embed v4, EU geographic inference profile (multilingual; eu-west-1 and EU peers). */
  modelId: "eu.cohere.embed-v4:0",
  /** Cohere Embed v4 output dimension (it supports 256, 512, 1024 and 1536). */
  dimension: 1024,
} as const;

/**
 * Foundation models whose `global.` route was verified to return the same vectors as their
 * `eu.` route (ADR 0006; `embeddingRoute` in models.ts). Only these switch route under
 * `global`; others keep the EU route, which `global` residency always allows. Cohere Embed v4
 * was verified on 2026-10-24 (dev, eu-west-1, 1024 dimensions): documents, queries and a
 * text+image input gave identical, deterministic vectors on both routes. Listed here because
 * this file is the single place that names an embedding model.
 */
export const ROUTE_EQUIVALENT_EMBEDDINGS: ReadonlySet<string> = new Set<string>([
  "cohere.embed-v4:0",
]);

/** Dimension of the `chunks.embedding` column; changes only through a schema regeneration. */
export const EMBEDDING_COLUMN_DIMENSION: number = DEFAULT_EMBEDDING.dimension;

export const embeddingConfigSchema = z.strictObject({
  modelId: z.string().trim().min(1),
  /** pgvector indexes (HNSW) support up to 2000 dimensions for `vector`. */
  dimension: z.int().min(1).max(2000),
});
export type EmbeddingConfig = z.infer<typeof embeddingConfigSchema>;

/**
 * Reads the environment's embedding config: defaults, overridden by `ESI_EMBEDDING_MODEL` and
 * `ESI_EMBEDDING_DIMENSION`. Throws when the dimension is invalid or differs from the column.
 */
export function embeddingConfigFromEnv(env: Record<string, string | undefined>): EmbeddingConfig {
  const dimension = env.ESI_EMBEDDING_DIMENSION?.trim();
  const config = embeddingConfigSchema.parse({
    modelId: env.ESI_EMBEDDING_MODEL?.trim() || DEFAULT_EMBEDDING.modelId,
    dimension: dimension ? Number(dimension) : DEFAULT_EMBEDDING.dimension,
  });
  if (config.dimension !== EMBEDDING_COLUMN_DIMENSION) {
    throw new Error(
      `ESI_EMBEDDING_DIMENSION=${config.dimension} does not match the chunks.embedding column ` +
        `(vector(${EMBEDDING_COLUMN_DIMENSION})): change DEFAULT_EMBEDDING and regenerate the schema`,
    );
  }
  return config;
}
